// ============================================================================
// LOCATION PICKER COMPONENT
// React-Select based location picker (filtered by contact)
// Date: October 17, 2025
// Updated: December 5, 2025 - Fixed to load selected location on mount
// ============================================================================

'use client';

import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { authService } from '@/lib/authService';

// ============================================================================
// TYPES
// ============================================================================

interface Location {
  id: number;
  location_name: string;
  location_type: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  is_primary: boolean;
  is_billing: boolean;
  is_shipping: boolean;
}

interface LocationPickerProps {
  contactId: number | null;
  value?: number | null;
  onChange: (locationId: number | null, location: Location | null) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  isDisabled?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function LocationPicker({
  contactId,
  value,
  onChange,
  placeholder = 'Select location...',
  className = '',
  required = false,
  isDisabled = false,
}: LocationPickerProps) {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const hasAutoSelectedRef = useRef(false);
  const prevContactIdRef = useRef<number | null>(null);
  
  // Load a specific location by ID (for pre-selected values)
  const loadLocationById = async (locationId: number) => {
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) return null;
      
      const response = await fetch(`/api/v1/finance/locations/${locationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success && data.data) {
        return data.data as Location;
      }
    } catch (error) {
      console.error('Error loading location by ID:', error);
    }
    return null;
  };
  
  // Load selected location on mount if value is provided
  useEffect(() => {
    const initSelectedLocation = async () => {
      if (value && value !== selectedLocation?.id) {
        const location = await loadLocationById(value);
        if (location) {
          setSelectedLocation(location);
        }
      }
    };
    initSelectedLocation();
  }, [value, selectedLocation?.id]);
  
  // Load locations when contactId changes
  useEffect(() => {
    // Track if contactId has changed (user selected new contact)
    const contactChanged = prevContactIdRef.current !== null && prevContactIdRef.current !== contactId;
    prevContactIdRef.current = contactId;
    
    if (!contactId) {
      setLocations([]);
      // Only clear selection if contact actually changed (not on initial load with existing value)
      if (contactChanged) {
        onChange(null, null);
        setSelectedLocation(null);
        hasAutoSelectedRef.current = false;
      }
      return;
    }
    
    const loadLocations = async () => {
      setLoading(true);
      try {
        const token = authService.getTokens()?.access_token;
        if (!token) {
          throw new Error('No auth token');
        }
        
        const response = await fetch(`/api/v1/contacts/${contactId}/locations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        const data = await response.json();
        
        if (data.success) {
          let loadedLocations = data.data || [];
          
          // If we have a selected location that's not in the list, add it
          if (selectedLocation && !loadedLocations.find((l: Location) => l.id === selectedLocation.id)) {
            loadedLocations = [selectedLocation, ...loadedLocations];
          }
          
          setLocations(loadedLocations);
          
          // Only auto-select if we don't have a value AND this is a fresh contact selection
          // AND we haven't auto-selected before for this contact
          if (!value && !hasAutoSelectedRef.current && contactChanged && loadedLocations.length > 0) {
            const primary = loadedLocations.find((l: Location) => l.is_primary);
            if (primary) {
              onChange(primary.id, primary);
            } else {
              // If no primary, select first location
              onChange(loadedLocations[0].id, loadedLocations[0]);
            }
            hasAutoSelectedRef.current = true;
          }
        } else {
          console.error('Failed to load locations:', data.error);
        }
      } catch (error) {
        console.error('Error loading locations:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadLocations();
  }, [contactId, selectedLocation, onChange, value]);
  
  // Format address for display
  const formatAddress = (location: Location): string => {
    const parts = [];
    if (location.city) parts.push(location.city);
    if (location.state) parts.push(location.state);
    return parts.length > 0 ? parts.join(', ') : '';
  };
  
  // Format label with badges
  const formatLabel = (location: Location): string => {
    let label = location.location_name;
    const address = formatAddress(location);
    if (address) {
      label += ` - ${address}`;
    }
    if (location.is_primary) {
      label += ' ⭐';
    }
    return label;
  };
  
  // Format options for react-select
  const options = locations.map(l => ({
    value: l.id,
    label: formatLabel(l),
    location: l
  }));
  
  // Find selected option - use selectedLocation state as fallback
  let selectedOption = options.find(o => o.value === value);
  if (!selectedOption && selectedLocation && selectedLocation.id === value) {
    selectedOption = {
      value: selectedLocation.id,
      label: formatLabel(selectedLocation),
      location: selectedLocation
    };
  }
  
  // Custom styles for react-select
  const customStyles = {
    control: (base: any, state: any) => ({
      ...base,
      minHeight: '42px',
      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.1)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
      },
      backgroundColor: !contactId ? '#f3f4f6' : 'white'
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#3b82f6' 
        : state.isFocused 
        ? '#eff6ff' 
        : 'white',
      color: state.isSelected ? 'white' : '#1f2937',
      '&:active': {
        backgroundColor: '#3b82f6'
      }
    })
  };
  
  return (
    <div className={className}>
      <Select
        value={selectedOption}
        onChange={(option) => {
          onChange(option?.value || null, option?.location || null);
        }}
        options={options}
        isLoading={loading}
        placeholder={contactId ? placeholder : 'Select a contact first...'}
        isClearable={!required}
        isSearchable
        isDisabled={isDisabled || !contactId}
        styles={customStyles}
        noOptionsMessage={() => 'No locations found for this contact'}
        loadingMessage={() => 'Loading locations...'}
      />
      
      {!contactId && (
        <p className="mt-1 text-sm text-gray-500">
          Select a contact first to see available locations
        </p>
      )}
    </div>
  );
}
